// ── packages — a folder of skills that arrived together ─────────────────────
// A skill is a folder (a SKILL.md with its references beside it), and a
// package is a folder of those: a repo someone keeps their skills in. People
// share skills that way — anthropic's library, obra's superpowers, a member's
// own dozen — and the commons used to flatten each one into loose cards, so a
// package of eleven read as eleven strangers.
//
// The package is DERIVED, never stored: every skill already says where it
// lives (`sourceUrl`), and two or more live skills from the same repo are a
// package. So everything brought before this existed groups itself on the next
// read — no migration, no second write, and nobody is written to again. A skill
// that is the only one from its repo stays a single card; the day a second one
// arrives from the same place, the two become a package on their own.
//
// Client-safe (pure): the community page groups on the client, the server uses
// the same functions to find the package a skill page belongs to.

import { originName, type CommunitySkill, type Origin } from "./types";

export interface PackRef {
  /** `owner/repo`, lowercased — the identity */
  key: string;
  owner: string;
  repo: string;
  /** the repo on github */
  url: string;
}

export interface SkillPack extends PackRef {
  /** what the card and the head call it */
  title: string;
  /** one quiet line: what this folder is */
  description: string;
  /** whose package it is — the keeper of its skills */
  origin: Origin;
  /** the member who brought it, when every skill in it was brought by one person */
  broughtBy?: { name: string; avatar?: string | null };
  /** its skills, newest first */
  skills: CommunitySkill[];
  /** the newest `updated` among them */
  updated?: string;
  /** the categories it covers, most common first */
  categories: string[];
  /** the uses of every skill in it, summed */
  uses: { score: number; total: number };
}

/** fewer than this from one repo is not a package — just a skill. */
export const PACK_MIN = 2;

const SLUG = /^[a-z0-9](?:[a-z0-9._-]{0,98}[a-z0-9_])?$/i;

/** the repo a skill lives in, when it lives on github. */
export function packRef(url: string | undefined | null): PackRef | null {
  if (!url) return null;
  const m = /^https?:\/\/(?:www\.)?github\.com\/([^/?#]+)\/([^/?#]+)/i.exec(url.trim());
  if (!m) return null;
  const owner = m[1];
  const repo = m[2].replace(/\.git$/i, "");
  if (!SLUG.test(owner) || !SLUG.test(repo)) return null;
  return { key: `${owner}/${repo}`.toLowerCase(), owner, repo, url: `https://github.com/${owner}/${repo}` };
}

/** where a package opens: inside the skills room, under its own name. */
export function packHref(p: Pick<PackRef, "owner" | "repo">): string {
  return `/community/skills/${encodeURIComponent(p.owner.toLowerCase())}/${encodeURIComponent(p.repo.toLowerCase())}`;
}

/** a repo named only for what it holds says nothing on a card ("skills",
 *  "agent-skills", "claude-skills"): those read as the keeper's skills. */
const GENERIC = /^(?:(?:agent|agents|ai|claude|claude-code|codex|llm|my|the|public|open)[-_ ]?)?(?:skills?|skill-pack|skillset|library)$/i;

function words(s: string): string {
  return s.replace(/[-_.]+/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
}

export function packTitle(ref: PackRef, keeper: string): string {
  if (GENERIC.test(ref.repo)) return `${words(keeper || ref.owner)} skills`;
  return words(ref.repo);
}

const byNewest = (a: CommunitySkill, b: CommunitySkill) => (b.updated ?? "").localeCompare(a.updated ?? "");

function buildPack(ref: PackRef, skills: CommunitySkill[]): SkillPack {
  const sorted = [...skills].sort(byNewest);
  const first = sorted[0];
  // the keeper most of its skills name (a package is somebody's)
  const names = new Map<string, number>();
  for (const s of sorted) names.set(originName(s.origin), (names.get(originName(s.origin)) ?? 0) + 1);
  const keeper = [...names.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? ref.owner;
  const origin = sorted.find((s) => originName(s.origin) === keeper)?.origin ?? first.origin;
  const brought = new Set(sorted.map((s) => s.broughtBy?.name ?? ""));
  const cats = new Map<string, number>();
  for (const s of sorted) cats.set(s.category, (cats.get(s.category) ?? 0) + 1);
  const categories = [...cats.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);
  const n = sorted.length;
  return {
    ...ref,
    title: packTitle(ref, keeper),
    description: `${n} skills kept together in one folder: ${categories.slice(0, 3).join(", ")}${categories.length > 3 ? " and more" : ""}. open any one to read it, or hand your ai the one it needs.`,
    origin,
    broughtBy: brought.size === 1 && sorted[0].broughtBy ? sorted[0].broughtBy : undefined,
    skills: sorted,
    updated: sorted.map((s) => s.updated).filter(Boolean).sort().pop(),
    categories,
    uses: sorted.reduce((acc, s) => ({ score: acc.score + (s.uses?.score ?? 0), total: acc.total + (s.uses?.total ?? 0) }), { score: 0, total: 0 }),
  };
}

/** every package on the shelf, keyed by `owner/repo`. */
export function packsOf(skills: CommunitySkill[]): Map<string, SkillPack> {
  const groups = new Map<string, { ref: PackRef; skills: CommunitySkill[] }>();
  for (const s of skills) {
    const ref = packRef(s.sourceUrl);
    if (!ref) continue;
    const g = groups.get(ref.key);
    if (g) g.skills.push(s);
    else groups.set(ref.key, { ref, skills: [s] });
  }
  const out = new Map<string, SkillPack>();
  for (const [key, g] of groups) if (g.skills.length >= PACK_MIN) out.set(key, buildPack(g.ref, g.skills));
  return out;
}

/** the package a skill sits in, if it sits in one. */
export function packOfSkill(skill: CommunitySkill, packs: Map<string, SkillPack>): SkillPack | null {
  const ref = packRef(skill.sourceUrl);
  return ref ? packs.get(ref.key) ?? null : null;
}

/** find a package by the two segments of its address (case-insensitive). */
export function findPack(packs: Map<string, SkillPack>, owner: string, repo: string): SkillPack | null {
  return packs.get(`${decodeURIComponent(owner)}/${decodeURIComponent(repo)}`.toLowerCase()) ?? null;
}
